import {StyleSheet, Text, View, ViewStyle} from "react-native";
import {ProfileFollowRequest, UserProfile} from "@/lib/types";
import {Color} from "@/lib/styles";
import {Image, ImageStyle} from "expo-image";
import {BASE_URL} from "@/lib/constants";
import AvatarBox from "@/lib/components/AvatarBox";
import Header from "@/lib/components/Header";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {useSession} from "@/lib/auth/auth";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import {useState} from "react";
import NavButton from "@/lib/components/NavButton";
import {useRouter} from "expo-router";
import {useAccount} from "@/lib/components/AccountProvider";

type Props = {
    username: string,
    detailed: boolean,
}

export default function UserProfilePage({username, detailed}: Props) {
    const {session} = useSession();
    const queryClient = useQueryClient();
    const router = useRouter();

    const [busy, setBusy] = useState(false);

    const profileQuery = useQuery<UserProfile>({
        queryKey: ['profile', username, session],
        queryFn: () => fetchBackend(`/profile/${username}`, "GET", session),
        retry: dataRetryPolicy,
    })

    const {account, accountError, accountLoading, refreshAccount} = useAccount();

    if (profileQuery.error) {
        return <ErrorPage error={profileQuery.error}/>
    } else if (accountError) {
        return <ErrorPage error={accountError}/>
    } else if (profileQuery.isPending || accountLoading || !account) {
        return <SpinnerPage/>
    }

    const profile = profileQuery.data;

    const headerStyle: ViewStyle = {
        backgroundColor: profile.avatar.color,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
    };

    const detailsStyle: ViewStyle = {
        borderColor: profile.avatar.color,
    };

    const imageStyle: ImageStyle = {
        borderColor: profile.avatar.contrast_color,
    };

    const hasFriends = profile.friends.length > 0;
    const hasFollowers = profile.followers.length > 0;
    const hasCommonFriends = profile.common_friends.length > 0;
    const hasPotentialRelationships = profile.potential_relationships.length > 0;

    const friendshipType = (() => {
        if (profile.avatar.id === account.avatar.id) {
            return "self";
        } else if (profile.is_following && profile.is_followed_by) {
            return "friends";
        } else if (profile.is_followed_by) {
            return "stalker";
        } else if (profile.is_following) {
            return "prey";
        } else {
            return "unknown";
        }
    })();

    const sendFollowRequest = (shouldFollow: boolean) => {
        setBusy(true);
        const request: ProfileFollowRequest = {follow: shouldFollow};
        fetchBackendNoContent(`/profile/${profile.avatar.username}`, "PATCH", session, request).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['profile', profile.avatar.username, session]});
            await refreshAccount();
        }).catch((_error) => {
            //setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    return (
        <View style={styles.outer}>
            <View style={styles.card}>
                <View style={[styles.header, headerStyle]}>
                    {profile.avatar.avatar_url &&
                        <Image style={[styles.image, imageStyle]} source={BASE_URL + profile.avatar.avatar_url}/>}
                    {!profile.avatar.avatar_url &&
                        <Text style={[styles.image, styles.placeholderImage, imageStyle]}>?</Text>}
                </View>
                <View style={[styles.details, detailsStyle]}>
                    <View style={styles.usernameAndID}>
                        <Text style={styles.username}>@{profile.avatar.username}</Text>
                        <Text style={styles.userID}>#{profile.avatar.id}</Text>
                    </View>
                    {friendshipType === "self" &&
                        <Text style={styles.text}>Det er deg!</Text>}
                    {friendshipType === "friends" &&
                        <Text style={styles.text}>Venner med deg</Text>}
                    {friendshipType === "prey" &&
                        <Text style={styles.text}>Venneforespørsel sendt</Text>}
                    {friendshipType === "stalker" &&
                        <Text style={styles.text}>Ønsker å bli venn</Text>}
                    {friendshipType === "unknown" &&
                        <Text style={styles.text}>Ingen relasjon</Text>}
                    {detailed &&
                        <Nav style={{justifyContent: "flex-start"}}>
                            <NavButton icon={"share"} href={`/u/profile/${profile.avatar.username}/share`}/>
                        </Nav>
                    }
                    {friendshipType !== "self" &&
                        <Nav>
                            {!profile.is_following &&
                                <ActionButton
                                    title={"Følg"}
                                    icon={"relation"}
                                    busy={busy}
                                    color={Color.Green}
                                    onPress={() => sendFollowRequest(true)}
                                />
                            }
                            {profile.is_following &&
                                <ActionButton
                                    title={"Følger"}
                                    icon={"relation"}
                                    busy={busy}
                                    color={Color.Background}
                                    backgroundColor={Color.Green}
                                    onPress={() => sendFollowRequest(false)}
                                />
                            }
                        </Nav>
                    }
                </View>
            </View>

            {detailed &&
                <View style={styles.footer}>
                    {hasFriends &&
                        <>
                            <Header title={`Mine venner ( ${profile.friends.length} )`}/>
                            <AvatarBox avatars={profile.friends}
                                       color={profile.avatar.color}
                                       onPress={() => router.push(`/u/profile/${profile.avatar.username}/friends`)}
                            />
                        </>
                    }
                    {hasFollowers &&
                        <>
                            <Header title={`Mine følgere ( ${profile.followers.length} )`}/>
                            <AvatarBox avatars={profile.followers}
                                       color={profile.avatar.color}
                                       onPress={() => router.push(`/u/profile/${profile.avatar.username}/followers`)}
                            />
                        </>
                    }
                    {hasCommonFriends &&
                        <>
                            <Header title={`Felles venner ( ${profile.common_friends.length} )`}/>
                            <AvatarBox avatars={profile.common_friends}
                                       color={profile.avatar.color}
                                       onPress={() => router.push(`/u/profile/${profile.avatar.username}/common`)}
                            />
                        </>
                    }
                    {hasPotentialRelationships &&
                        <>
                            <Header title={`Omgangskrets ( ${profile.potential_relationships.length} )`}/>
                            <AvatarBox avatars={profile.potential_relationships}
                                       color={profile.avatar.color}
                                       onPress={() => router.push(`/u/profile/${profile.avatar.username}/network`)}
                            />
                        </>
                    }
                </View>
            }

        </View>
    )
}

const styles = StyleSheet.create({
    outer: {
        gap: 8,
        justifyContent: "flex-start",
        flexDirection: "column",
    },
    card: {
        flexDirection: "column",
        borderRadius: 16,
        backgroundColor: Color.Background,
    },
    header: {
        gap: 8,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-around",
        backgroundColor: Color.PrimaryText,
        color: Color.Background,
        padding: 16,
        fontSize: 24,
    },
    usernameAndID: {
        width: "100%",
        gap: 8,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
    },
    details: {
        justifyContent: "flex-start",
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 12,
        backgroundColor: Color.Background,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
    },
    footer: {
        gap: 8,
    },
    image: {
        borderWidth: 2,
        borderRadius: 72,
        width: 144,
        height: 144,
    },
    placeholderImage: {
        borderStyle: "dashed",
        fontSize: 96,
        lineHeight: 144,
        textAlign: "center",
        backgroundColor: Color.BackgroundContainer,
        color: Color.Faded,
    },
    username: {
        color: Color.PrimaryText,
        fontWeight: "600",
        fontSize: 24,
        marginBottom: 8,
    },
    userID: {
        color: Color.Faded,
        fontSize: 14,
    },
    headerText: {
        color: Color.PrimaryText,
        fontSize: 16,
        lineHeight: 32,
        fontWeight: 600,
        marginTop: 8,
        width: "100%",
    },
    text: {
        color: Color.PrimaryText,
        fontSize: 16,
        width: "100%",
    }
});